<template>
  <div class="page has-compressed-header">
    <BotPreviewModal
      id="answer-preview-modal"
      v-if="showBotPreviewModal"
      @cancel="showBotPreviewModal = false"
      :answerToPreview="answerId"
    />
    <n-loading-spinner v-if="!loadedAnswer || !loadedIntentList" />
    <section v-if="loadedAnswer && loadedIntentList" class="section primary-header">
      <nav class="level container is-fluid">
        <div class="level-left">
          <div class="back-arrow" @click="handleBackNavigation">
            <BackArrow class="icon-1-5x"></BackArrow>
          </div>
          <h4 class="title is-4 answer-title">
            <n-tooltip position="bottom" :text="faqNameErrorText">
              <EditableText
                :class="{ invalid: $v.faqName.$error }"
                placeholder="Enter the Frequently Asked Question..."
                ref="titleInput"
                :value="faqName"
                @text-updated="setFaqName"
                minWidth="100px"
                maxWidth="600px"
              >
              </EditableText>
            </n-tooltip>
            <div class="flow-status is-flex">
              <n-badge v-if="currentAnswerStatus === 'live'" variant="success">Live</n-badge>
              <n-badge v-if="currentAnswerStatus === 'paused'" variant="secondary">Paused</n-badge>
              <n-badge v-if="currentAnswerStatus === 'draft'" variant="default">Draft</n-badge>
            </div>
          </h4>
        </div>
        <div class="level-right">
          <p class="level-item">
            <n-tooltip position="bottom" :text="saveAnswerHoverMessage">
              <n-button
                id="save-button"
                variant="secondary"
                :disabled="!isSaveButtonEnabled"
                :loading="isSaveInProgress"
                @click="saveAnswer()"
              >
                <div class="button-content">
                  <SaveIcon />
                  Save Changes
                </div>
              </n-button>
            </n-tooltip>
          </p>

          <p class="level-item">
            <n-tooltip position="bottom" :text="previewFlowHoverMessage">
              <n-button
                variant="secondary--outline"
                :disabled="!isPreviewButtonEnabled"
                @click="openPreview()"
                id="preview-bot-button"
              >
                <div class="button-content">
                  <PlayIcon />
                  Preview
                </div>
              </n-button>
            </n-tooltip>
          </p>

          <AdvancedTooltip v-if="isMakeLiveButtonVisible() && !isBotStudioEnabled()" class="level-item">
            <template v-slot:element>
              <p>
                <n-button
                  variant="success"
                  :disabled="!canMakeLive"
                  @click="showMakeAnswerLiveConfirmation()"
                  id="make-live-button"
                >
                  <div class="button-content"><PlayIcon /> Make Live</div>
                </n-button>
              </p>
            </template>
            <template v-slot:content>
              <div><strong>Upgrade to unlock this feature</strong></div>
              <div>Get flows, FAQs and more</div>
            </template>
            <template v-slot:footer>
              <button class="button is-primary is-outlined is-fullwidth" @click="upgradeButtonClicked()">
                Upgrade
              </button>
            </template>
          </AdvancedTooltip>

          <p class="level-item">
            <n-tooltip
              v-if="isMakeLiveButtonVisible() && isBotStudioEnabled()"
              position="bottom"
              :text="makeLiveHoverMessage"
            >
              <n-button
                variant="success"
                :disabled="!canMakeLive"
                @click="showMakeAnswerLiveConfirmation()"
                id="make-live-button"
              >
                <div class="button-content">
                  <PlayIcon />
                  Make Live
                </div>
              </n-button>
            </n-tooltip>
          </p>

          <p class="level-item">
            <DropDownMenu :goLeft="true" :useColourOnActive="true">
              <a v-if="isPauseOptionVisible()" @click="pauseAnswer" class="dropdown-item" id="pause-answer-option"
                ><PauseIcon />Pause FAQ</a
              >
              <a @click="deleteAnswer" class="dropdown-item" id="delete-answer-option"><DeleteIcon />Delete</a>
            </DropDownMenu>
          </p>
        </div>
      </nav>
    </section>

    <section
      v-if="loadedAnswer && loadedIntentList"
      class="section content-container"
      :class="{ 'is-disabled': isSaveInProgress }"
    >
      <div class="question-answer-container">
        <div class="questions-container">
          <div class="sub-heading sub-heading-left">
            <h5 class="title is-6">Frequently Asked Question</h5>
          </div>

          <div class="question-list">
            <FaqIntentPicker :faqName="faqName" :intentId="intentId" @input="handleIntentChange" :intents="intents" />
            <n-loading-spinner v-if="!loadedFullIntent" />
            <div v-if="loadedFullIntent">
              <h2><strong>Example Utterances</strong></h2>
              <UtteranceList
                v-model="questions"
                term="utterance"
                :is-editable="!isSaveInProgress"
                @input="onAnswerChange"
              />
            </div>
          </div>
        </div>

        <div class="answer-container">
          <div class="sub-heading sub-heading-right">
            <h5 class="title is-6">Answer</h5>
          </div>

          <div class="answer">
            <Draggable
              v-model="reorderableStates"
              class="state-list"
              handle=".island-badge"
              @end="clearTransform"
              item-key="id"
            >
              <template #item="{ element: state }">
                <div class="state" :key="state.id">
                  <component :is="getStateComponent(state)" :value="state" @state-updated="stateUpdated" />
                  <div class="state-menu">
                    <a class="delete is-small delete-state" @click="deleteState(state)"></a>
                  </div>
                </div>
              </template>
            </Draggable>

            <DropDownMenu
              class="add-state-container"
              dropDownIcon="big-add-circle"
              @isUp="setIsStatePickerUp"
              :class="{ 'is-up': isStatePickerUp }"
            >
              <a @click="addMessageState()" class="dropdown-item">
                <TextIcon></TextIcon>
                Message
              </a>

              <a @click="addAppIntegrationState()" class="dropdown-item">
                <span class="cube-icon">
                  <font-awesome-icon icon="cube"></font-awesome-icon>
                </span>
                App Action
              </a>

              <a @click="addGoToState()" class="dropdown-item" v-if="!containsGoToState()">
                <GoToIcon />
                Go To
              </a>
            </DropDownMenu>
            <p class="help is-danger" v-if="$v.answerStates.$error && !$v.answerStates.required">
              You must add an answer
            </p>
            <div v-if="containsGoToState()">
              <div class="state" v-for="state in endStates" :key="state.id">
                <component :is="getStateComponent(state)" :value="state" @state-updated="stateUpdated" />
                <div class="state-menu">
                  <a class="delete is-small delete-state" @click="deleteState(state)"></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ConfirmationModal
      id="discard-changes-modal"
      ref="discardModal"
      v-if="showLeaveModal"
      confirmationTitle="Discard Changes"
      :confirmationMessage="getLeaveConfirmationMessage()"
      @confirm="discardAnswer"
      @cancel="showLeaveModal = false"
    ></ConfirmationModal>

    <ConfirmationModal
      id="make-live-modal"
      v-if="showMakeLiveModal"
      confirmationTitle="Make Live"
      :confirmationMessage="getMakeLiveConfirmationMessage()"
      @confirm="makeAnswerLive"
      @cancel="showMakeLiveModal = false"
    ></ConfirmationModal>

    <ConfirmationModal
      id="pause-answer-modal"
      v-if="showPauseAnswerModal"
      confirmationTitle="Pause FAQ"
      :confirmationMessage="getPauseConfirmationMessage()"
      @confirm="onPauseAnswerConfirmed"
      @cancel="showPauseAnswerModal = false"
    ></ConfirmationModal>

    <ConfirmationModal
      id="delete-modal"
      v-if="showDeleteModal"
      confirmationTitle="Delete FAQ"
      :confirmationMessage="getDeleteAnswerConfirmationMessage()"
      :typeConfirmationValidationMessage="faqName"
      confirmButtonLabel="Delete"
      @confirm="onDeleteAnswerConfirmed()"
      @cancel="showDeleteModal = false"
    />

    <UpgradeModal v-if="showUpgradeModal" @close="showUpgradeModal = false" />
  </div>
</template>

<script lang="ts" setup>
import { answersApi } from '../services/answers';
import { QuestionViewModel } from '../models/answers/questionViewModel';
import { nanoid } from 'nanoid';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import EditableText from '@/shared/components/EditableText.vue';
import Toast from '@/shared/services/toast';
import Draggable from 'vuedraggable';
import { StateViewModel } from '../models/designer';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import TextIcon from 'vue-material-design-icons/FormatTitle.vue';
import GoToIcon from '@/shared/icons/GoToIcon.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import { AnswerViewModel } from '@/features/bots/models/answers/answerViewModel';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import BackArrow from 'vue-material-design-icons/ArrowLeft.vue';
import SaveIcon from 'vue-material-design-icons/ContentSave.vue';
import PauseIcon from 'vue-material-design-icons/Pause.vue';
import PlayIcon from 'vue-material-design-icons/Play.vue';
import appInsights from '@/shared/services/appInsights';
import BotPreviewModal from '@/features/bots/components/botTestTool/BotPreviewModal.vue';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import AdvancedTooltip from '@/shared/components/AdvancedTooltip.vue';
import UpgradeModal from '@/features/bots/components/UpgradeModal.vue';
import FaqIntentPicker from '@/features/bots/components/FaqIntentPicker.vue';
import { CREATE_INTENT, GET_INTENT, LOAD_INTENTS, UPDATE_INTENT } from '@/store/intents/actionTypes';
import { IntentViewModel } from '../models/intents/intentViewModel';
import { SaveIntentViewModel } from '../models/intents/saveIntentViewModel';
import { IntentListItemViewModel } from '../models/intents/intentListItemViewModel';
import { LOAD_ANSWER, UPDATE_ANSWER } from '@/store/answers/actionTypes';
import { UpdateAnswerViewModel } from '../models/answers/updateAnswerViewModel';
import { useStore } from 'vuex';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useRoute, useRouter, onBeforeRouteLeave, RouteLocationNormalized } from 'vue-router';
import TextComponent from '@/features/bots/components/designer/TextComponent.vue';
import AppIntegrationComponent from '@/features/bots/components/designer/AppIntegrationComponent.vue';
import GoToComponent from '@/features/bots/components/designer/GoToComponent.vue';

const COMPONENTS = {
  TextComponent,
  AppIntegrationComponent,
  GoToComponent,
};

const validIntentName = (value: string) => {
  return value.search(/[<>*%&:\\$]/) === -1;
};

const router = useRouter();
const route = useRoute();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const isWorkspaceFeatureEnabled = (feature: string) => computed(() => store.getters.isWorkspaceFeatureEnabled(feature));
const isAnswerTransitioningToAnInactiveFlow = computed(() => store.getters.isAnswerTransitioningToAnInactiveFlow);

const loadAnswer = (answerId: string): Promise<AnswerViewModel> => store.dispatch(LOAD_ANSWER, answerId);
const updateAnswer = (answerId: string, request: UpdateAnswerViewModel): Promise<AnswerViewModel> =>
  store.dispatch(UPDATE_ANSWER, { answerId, request });
const getIntent = (id: string): Promise<IntentViewModel> => store.dispatch(GET_INTENT, id);
const createIntent = (intent: SaveIntentViewModel): Promise<IntentViewModel> => store.dispatch(CREATE_INTENT, intent);
const updateIntent = (id: string, intent: SaveIntentViewModel): Promise<IntentViewModel> =>
  store.dispatch(UPDATE_INTENT, { id, intent });
const loadIntents = (forceLoad: boolean = false): Promise<IntentListItemViewModel[]> =>
  store.dispatch(LOAD_INTENTS, forceLoad);

const questions = ref<QuestionViewModel[]>([]);
const faqName = ref('');
const currentAnswerStatus = ref<FlowStatus>(FlowStatus.Draft);
const hasMadeChanges = ref(false);
const hasLiveParentFlows = ref(false);
const isSaveInProgress = ref(false);
const loadedAnswer = ref(false);
const loadedFullIntent = ref(false);
const loadedIntentList = ref(false);
const isStatePickerUp = ref(false);
const intentId = ref<string>();
const intent = ref<Partial<IntentViewModel>>();
const intents = ref<IntentListItemViewModel[]>([]);
const showLeaveModal = ref(false);
const showDeleteModal = ref(false);
const showMakeLiveModal = ref(false);
const showBotPreviewModal = ref(false);
const showPauseAnswerModal = ref(false);
const showUpgradeModal = ref(false);
const existingAnswer = ref<AnswerViewModel>();
let navigationNext: ((value?: boolean) => void) | null = null; // Store the next callback
const discardModal = ref<typeof ConfirmationModal | null>(null);

const answerStates = ref<StateViewModel[]>([]);

const endStates = computed<StateViewModel[]>(
  () => answerStates.value.filter((s: StateViewModel) => s.component === 'GoTo') || [],
);

const reorderableStates = computed({
  get() {
    return answerStates.value.filter((s: StateViewModel) => s.component !== 'GoTo');
  },
  set(newOrder: StateViewModel[]) {
    // Preserve GoTo states
    const goToStates = answerStates.value.filter((s: StateViewModel) => s.component === 'GoTo');
    // Update answerStates with new order followed by GoTo states
    answerStates.value = [...newOrder, ...goToStates];

    onAnswerChange();
  },
});

const validations = {
  faqName: {
    required,
    validIntentName,
  },
  answerStates: {
    required,
  },
  intent: {
    required,
  },
  questions: {
    required,
  },
};

const $v = useVuelidate(validations, { faqName, answerStates, intent, questions });

const faqNameErrorText = computed(() => {
  const vuelidateFaqNameField = $v.value.faqName;

  if (vuelidateFaqNameField.$error && vuelidateFaqNameField.required && !vuelidateFaqNameField.validIntentName) {
    return 'Must not contain &lt;, &gt;, *, %, &amp;, :, \\, $';
  }
  return '';
});

const previewFlowHoverMessage = computed(() =>
  hasMadeChanges.value ? 'Cannot preview an FAQ with unsaved changes' : '',
);
const saveAnswerHoverMessage = computed(() => (!hasMadeChanges.value ? 'No changes to save' : ''));

const makeLiveHoverMessage = computed(() => {
  if (isAnswerTransitioningToAnInactiveFlow.value) {
    return 'Must not transition to flow that is not live';
  }
  if (hasMadeChanges.value) {
    return 'Cannot make FAQ live with unsaved changes';
  }
  return '';
});

const isPreviewButtonEnabled = computed(() => !hasMadeChanges.value && !isSaveInProgress.value && intentId.value);

const pauseAnswerErrorMessage = computed(() => {
  if (hasLiveParentFlows.value) {
    return 'Cannot pause an FAQ that is transitioned to by a live flow';
  }
  if (hasMadeChanges.value) {
    return 'Cannot pause an FAQ with unsaved changes';
  }
  return '';
});

const isSaveButtonEnabled = computed(() => !isSaveInProgress.value && hasMadeChanges.value);
const canMakeLive = computed(
  () =>
    !hasMadeChanges.value &&
    !isSaveInProgress.value &&
    isBotStudioEnabled() &&
    intentId.value &&
    !isAnswerTransitioningToAnInactiveFlow.value,
);
const canPauseAnswer = computed(
  () => currentAnswerStatus.value === FlowStatus.Live && !hasLiveParentFlows.value && !hasMadeChanges.value,
);
const canDeleteAnswer = computed(() => !hasLiveParentFlows.value);
const answerId = computed(() => <string>route.params.id);

onMounted(() => Promise.all([fetchAnswer(), fetchIntents()]));
onBeforeUnmount(() => (navigationNext = null));

function onAnswerChange() {
  hasMadeChanges.value = true;
}

async function saveAnswer() {
  $v.value.$touch();

  if ($v.value.$invalid || answerStates.value.some((s) => s.invalid)) {
    Toast.showError('Fix the errors in red before saving');
    return;
  }
  try {
    appInsights.trackEvent(
      { name: 'Save answer clicked' },
      { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
    );
    isSaveInProgress.value = true;
    faqName.value = faqName.value.trim();

    if (!intentId.value) {
      await createNewIntent();
    } else {
      await updateExistingIntent();
    }

    await updateAnswer(answerId.value!, {
      title: faqName.value,
      intentId: intentId.value!,
      answerStates: answerStates.value,
      status: currentAnswerStatus.value,
    });

    hasMadeChanges.value = false;
    Toast.showSuccess('Saved FAQ');
  } catch (ex: any) {
    Toast.showError(ex.message);
  } finally {
    isSaveInProgress.value = false;
  }
}

function openPreview() {
  appInsights.trackEvent(
    { name: 'Preview clicked' },
    { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
  );
  showBotPreviewModal.value = true;
}

function showMakeAnswerLiveConfirmation() {
  appInsights.trackEvent(
    { name: 'Make live clicked' },
    { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
  );
  showMakeLiveModal.value = true;
}

function upgradeButtonClicked() {
  showUpgradeModal.value = true;
}

function deleteAnswer() {
  if (!canDeleteAnswer.value) {
    Toast.showError('Cannot delete an FAQ that is transitioned to by another flow');
  } else {
    showDeleteModal.value = true;
  }
}

function handleBackNavigation() {
  window.setTimeout(() => {
    router.push({ name: 'bots-answers' });
  }, 100); // Wait for DOM/state updates to complete, next tick doesn't do it as the browser needs to un-focus on a potential input
}

onBeforeRouteLeave((to, from, next) => {
  if (hasMadeChanges.value) {
    setupLeaveModal(next);
  } else {
    next();
  }
});

function setupLeaveModal(next: (value?: boolean) => void) {
  showLeaveModal.value = true;
  navigationNext = next;
}

async function discardAnswer() {
  showLeaveModal.value = false;
  if (navigationNext) {
    navigationNext(true); // Proceed with navigation
    navigationNext = null; // Clear after use
  }
}

function pauseAnswer() {
  appInsights.trackEvent(
    { name: 'Pause answer clicked' },
    { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
  );
  if (!canPauseAnswer.value) {
    Toast.showError(pauseAnswerErrorMessage.value);
    return;
  }
  showPauseAnswerModal.value = true;
}

async function makeAnswerLive() {
  try {
    isSaveInProgress.value = true;
    const savedAnswer = await updateAnswer(answerId.value, {
      status: FlowStatus.Live,
    });
    currentAnswerStatus.value = savedAnswer.status;
    appInsights.trackEvent(
      { name: 'Made answer live' },
      { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
    );
    Toast.showSuccess(`Made FAQ ${faqName.value} live`);
  } catch (error) {
    Toast.showError(`Failed to make FAQ ${faqName.value} live`);
  }
  isSaveInProgress.value = false;
  showMakeLiveModal.value = false;
}

async function onDeleteAnswerConfirmed() {
  showDeleteModal.value = false;
  try {
    appInsights.trackEvent(
      { name: 'Delete answer clicked' },
      { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
    );
    await answersApi.deleteAnswer(currentWorkspace.value.id, answerId.value);
    hasMadeChanges.value = false;
    await router.push({ name: 'bots-answers' });
    Toast.showSuccess('Deleted FAQ');
  } catch (ex: any) {
    Toast.showError('Failed to delete FAQ : ' + ex.message);
  }
}

async function onPauseAnswerConfirmed() {
  try {
    isSaveInProgress.value = true;
    const savedAnswer = await updateAnswer(answerId.value, {
      status: FlowStatus.Paused,
    });
    currentAnswerStatus.value = savedAnswer.status;
    appInsights.trackEvent(
      { name: 'Answer paused' },
      { answerId: answerId.value, workspaceId: currentWorkspace.value.id },
    );
    Toast.showSuccess(`Paused ${faqName.value}`);
  } catch (error) {
    Toast.showError(`Failed to pause FAQ ${faqName.value}`);
  }
  isSaveInProgress.value = false;
  showPauseAnswerModal.value = false;
}

function getDeleteAnswerConfirmationMessage() {
  return `Are you sure you want to delete the <b>${HtmlFormatter.escapeHtmlCharacters(faqName.value)}</b> FAQ? The action is irreversible. Please type the name of the FAQ to confirm:`;
}

function setFaqName(text: string, previousValue: string) {
  if (text !== previousValue) {
    faqName.value = text;
    onAnswerChange();
  }
}

const getStateComponent = (state: StateViewModel) => {
  const name = `${state.component}Component`;
  return (COMPONENTS as any)[name] || name;
};

function addMessageState() {
  addState({
    id: `state-${nanoid(7)}`,
    component: 'Text',
    text: '',
  });
  onAnswerChange();
}

function addAppIntegrationState() {
  addState({
    id: `state-${nanoid(7)}`,
    component: 'AppIntegration',
  });
  onAnswerChange();
}

function addGoToState() {
  answerStates.value.push({
    id: `state-${nanoid(7)}`,
    component: 'GoTo',
    transition: {
      type: undefined,
      payload: undefined,
    },
    invalid: true,
  });
  onAnswerChange();
}

function deleteState(state: StateViewModel) {
  answerStates.value = answerStates.value.filter((s) => s.id !== state.id);
  onAnswerChange();
}

function stateUpdated(state: StateViewModel) {
  const index = answerStates.value.findIndex((s) => s.id === state.id);
  answerStates.value[index] = state;
  onAnswerChange();
}

function clearTransform(event: any) {
  // this is a fix for the modal within a state not appearing correctly after the state is dragged
  // the modal is position:fixed which can be relative to a parent that has a transform style, which we don't want.
  // see https://github.com/SortableJS/Sortable/issues/1687
  event.item.style.transform = null;
}

function setIsStatePickerUp(val: boolean) {
  isStatePickerUp.value = val;
}

function isMakeLiveButtonVisible() {
  return isStatus(FlowStatus.Paused, FlowStatus.Draft);
}

function isPauseOptionVisible() {
  return isStatus(FlowStatus.Live);
}

function getLeaveConfirmationMessage() {
  return `All changes you've made to <b>${HtmlFormatter.escapeHtmlCharacters(faqName.value)}</b> will be discarded. <br/>Are you sure you want to leave the page?`;
}

function getMakeLiveConfirmationMessage() {
  return `This will make the FAQ active to your audience. Are you sure you want to continue?`;
}

function getPauseConfirmationMessage() {
  return `This will prevent the FAQ from being triggered. Are you sure you want to continue?`;
}

function isBotStudioEnabled() {
  return isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED).value;
}

async function handleIntentChange(changedIntent: Partial<IntentListItemViewModel>) {
  const hasChangedIntent = changedIntent.id !== intentId.value && changedIntent.title !== intent.value?.title;
  const isInitialChangeToFaqNameIntent = !intent.value && changedIntent.title === faqName.value;
  if (hasChangedIntent && !isInitialChangeToFaqNameIntent) {
    onAnswerChange();
  }
  if (intentId.value !== changedIntent.id) {
    questions.value = [{ id: nanoid(7), text: '' }];
  }
  intent.value = changedIntent;
  intentId.value = changedIntent.id!;
  loadedFullIntent.value = false;
  await loadFullIntent();
  loadedFullIntent.value = true;
}

async function loadFullIntent() {
  if (!intentId.value) {
    return;
  }
  intent.value = await getIntent(intentId.value);
  questions.value = intent.value.utterances || [{ id: nanoid(7), text: '' }];
}

function containsGoToState() {
  return answerStates.value?.some((s) => s.component === 'GoTo');
}

async function fetchAnswer() {
  existingAnswer.value = await loadAnswer(answerId.value);
  intentId.value = existingAnswer.value.intentId;
  faqName.value = existingAnswer.value.title;

  answerStates.value = existingAnswer.value.answerStates.map((s) => {
    s.id = `state-${nanoid(7)}`;
    return s;
  });

  hasLiveParentFlows.value = existingAnswer.value.parentFlowIds && existingAnswer.value.parentFlowIds.length > 0;
  currentAnswerStatus.value = existingAnswer.value.status;
  if (answerStates.value.length === 0) {
    addMessageState();
  }

  loadedAnswer.value = true;

  await nextTick(() => {
    hasMadeChanges.value = false;
  });
}

async function fetchIntents() {
  intents.value = await loadIntents(true);
  loadedIntentList.value = true;
}

function isStatus(...allowedStatuses: FlowStatus[]): boolean {
  return allowedStatuses.includes(currentAnswerStatus.value);
}

async function createNewIntent() {
  if (!intent.value || !intent.value.title) {
    return;
  }
  const newIntent = await createIntent({
    title: intent.value.title,
    utterances: questions.value.map((q) => q.text),
  });
  intentId.value = newIntent.id;
}

async function updateExistingIntent() {
  if (!intent.value || !intent.value.title) {
    return;
  }
  await updateIntent(intentId.value!, {
    title: intent.value.title,
    utterances: questions.value.map((q) => q.text),
  });
}

function addState(state: StateViewModel): void {
  if (endStates.value.length > 0) {
    answerStates.value.splice(answerStates.value.length - 1, 0, state);
    return;
  }
  answerStates.value.push(state);
}
</script>

<style scoped lang="scss">
.back-arrow {
  display: flex;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;

  background-color: $color-netcall-deep-sea;
  border-radius: 50%;

  color: $color-netcall-jonquil;

  &:hover {
    color: $color-netcall-deep-sea;
    background-color: $color-netcall-jonquil;
  }

  .icon-1-5x {
    bottom: 2px;
  }
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.section {
  padding: 0;
}

.content-container {
  padding: 0;
  display: flex;
}

.answer-title {
  margin-left: 0.5em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 10px;

  .flow-status {
    align-items: center;
  }
}

.dropdown-item {
  .material-design-icon {
    margin-right: 0.5rem;
  }
}

.add-state-container {
  .dropdown-item {
    .material-design-icon {
      margin-right: 0;
    }
  }
}

.sub-heading {
  padding-left: 2rem;
}

.question-answer-container {
  flex: 1;
  display: flex;
}

.questions-container {
  flex: 1;
  background: white;
  .question-list {
    padding: 0 1.5rem;
    h2 {
      margin-left: 0.5rem;
    }
  }
}

.answer-container {
  flex: 1;
  background: #dbdfe8;
}

.delete-button a {
  color: $color-netcall-jonquil;

  &:hover {
    color: #eadc8e;
  }
}

.invalid {
  :deep(span) {
    text-decoration: underline red;
  }
}

.answer {
  display: flex;
  flex-direction: column;
  width: 80%;
  margin: auto;

  p.help {
    align-self: center;
  }
}

.state-list {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.state {
  margin-top: 1rem;

  display: flex;
  position: relative;
  min-height: 60px;
  background: white;
  border-radius: 5px;
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.1);

  &:focus-within {
    z-index: 2;
  }

  .state-menu {
    position: absolute;
    top: 5px;
    right: 5px;
    color: rgba(10, 10, 10, 0.2);

    &:hover {
      color: rgba(10, 10, 10, 0.4);
    }
  }
}

.add-state-container {
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
  align-self: center;
  display: inline;

  span {
    padding-right: 30px;
  }

  .cube-icon {
    font-size: 13px;
    padding-right: 1rem;
  }
}

:deep(.invalid-warning) {
  position: absolute;
  right: -2.5em;
  top: 50%;
  transform: translate(-50%, -50%);
  color: red;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.is-up {
  .dropdown-menu {
    margin-bottom: 5px;
  }
}
</style>
